/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { css } from 'aphrodite';
import { chain, concat, Dictionary, isEmpty, kebabCase, partition } from 'lodash';

import { BlendBridge } from 'blb-types/blendBridge';
import { titleCase } from 'blb-utils/format';
import { cardWrapperStyles } from 'blb-components/AuthenticatedApp/DashboardPage/SectionCard';
import { DocumentsCard } from 'blb-components/AuthenticatedApp/DashboardPage/DocumentsCard';
import {
  DocumentCardLabel,
  DocumentDesignation,
  DocumentTitles,
  EcloseDocumentTypeLabels,
} from 'blb-components/AuthenticatedApp/DashboardPage/DocumentsCard/constants';
import { ENoteDocumentCard } from 'blb-components/AuthenticatedApp/DashboardPage/docUtils/ENoteDocumentCard';
import * as constants from '../../common/constants';
import { ECloseDocumentTypes } from '../../common/constants';
import * as docUtils from './documents';
import { ENoteData } from '@blend-types/eclose';
import { getConfig } from 'blb-utils/config';

const getDocTypeMap = (
  docDesignation: DocumentDesignation,
  label?: DocumentCardLabel | 'Ink Sign',
): { documentTypeLabel: string; key: string; title: string } => {
  if (docDesignation === 'DAY_OF' && label) {
    return {
      documentTypeLabel: {
        'Ink Sign': EcloseDocumentTypeLabels.INK_SIGN_CLOSING_PACKAGE,
        'Lender RON': EcloseDocumentTypeLabels.RON_CLOSING_PACKAGE,
        RON: EcloseDocumentTypeLabels.RON_CLOSING_PACKAGE,
      }[label],
      key: 'ReceivedDayOfDocs',
      title: {
        'Ink Sign': DocumentTitles.LENDER_INK_SIGN_CLOSING_DOCUMENTS,
        'Lender RON': DocumentTitles.LENDER_RON_CLOSING_DOCUMENTS,
        RON: DocumentTitles.RON_CLOSING_DOCUMENTS,
      }[label],
    };
  }
  return {
    REJECTED_RON: {
      documentTypeLabel: EcloseDocumentTypeLabels.REJECTED_RON_CLOSING_PACKAGE,
      key: 'ReceivedRejectedRONDocs',
      title: DocumentTitles.REJECTED_RON_CLOSING_DOCUMENTS,
    },
    SIGN_AHEAD: {
      documentTypeLabel: EcloseDocumentTypeLabels.ESIGN_CLOSING_PACKAGE,
      key: 'ReceivedSignAheadDocs',
      title: DocumentTitles.LENDER_ESIGN_CLOSING_DOCUMENTS,
    },
    SIGNED: {
      documentTypeLabel: EcloseDocumentTypeLabels.SIGNED_CLOSING_PACKAGE,
      key: 'SignedClosingDocuments',
      title: DocumentTitles.SIGNED_CLOSING_DOCUMENTS,
    },
    UNDETERMINED: {
      documentTypeLabel: EcloseDocumentTypeLabels.UNDETERMINED_CLOSING_PACKAGE,
      key: 'LenderUndetermined',
      title: DocumentTitles.LENDER_CLOSING_DOCUMENTS,
    },
  }[docDesignation as Exclude<DocumentDesignation, 'DAY_OF'>];
};

export const isRONTransactionCompleted = (loan: BlendBridge.API.Loan): boolean => {
  const activeRonTransaction = loan.closing?.ronTransactions.find(({ active }) => active);
  return activeRonTransaction ? activeRonTransaction.status === 'COMPLETED' : false;
};

export const renderReceivedDocsOfType = ({
  auditEvents,
  docDesignation,
  documents,
  label,
  statusTag,
}: {
  auditEvents?: EClose.AuditEventResponse[];
  docDesignation: DocumentDesignation;
  documents: BlendBridge.API.Document[];
  label?: DocumentCardLabel;
  statusTag?: JSX.Element;
}): JSX.Element | null => {
  if (isEmpty(documents)) return null;

  const { title, key, documentTypeLabel } = getDocTypeMap(docDesignation, label);

  return (
    <div className={css(cardWrapperStyles.singleCardWrapper)}>
      <DocumentsCard
        auditEvents={auditEvents}
        documentTypeLabel={documentTypeLabel}
        documents={documents}
        headerChildren={statusTag}
        id={`it-${key}`}
        isUploaded={false}
        key={`it-${key}`}
        title={title}
      />
    </div>
  );
};

export const renderSplitReceivedDocs = (
  receivedDocs: BlendBridge.API.Document[],
  docIdToRefMap: Dictionary<EClose.API.DocumentReferenceResponse>,
  dayOfLabel: DocumentCardLabel,
  auditEvents?: EClose.AuditEventResponse[],
  signAheadStatusTag?: JSX.Element,
): React.ReactNode => (
  <>
    {renderReceivedDocsOfType({
      auditEvents,
      docDesignation: 'UNDETERMINED',
      documents: docUtils.filterDocsByDesignations(receivedDocs, docIdToRefMap, ['UNDETERMINED']),
    })}
    {/* when documents come in via API they will be here */}
    {renderReceivedDocsOfType({
      auditEvents,
      docDesignation: 'SIGN_AHEAD',
      documents: docUtils.filterDocsByDesignations(receivedDocs, docIdToRefMap, ['SIGN_AHEAD']),
      statusTag: signAheadStatusTag,
    })}

    {renderReceivedDocsOfType({
      auditEvents,
      docDesignation: 'DAY_OF',
      documents: docUtils.filterDocsByDesignations(receivedDocs, docIdToRefMap, ['DAY_OF']),
      label: dayOfLabel,
    })}
  </>
);

export const renderENoteDocumentCard = (
  eNoteData: ENoteData | undefined,
  eNoteDocs: BlendBridge.API.Document[],
): React.ReactNode => {
  // currently, there is at most one NOTE doc. When an eNote is regenerated, the old one is deleted.
  const eNoteDoc = eNoteDocs.length > 0 ? eNoteDocs[0] : undefined;
  return eNoteData?.doesActiveENoteExist ? <ENoteDocumentCard eNoteData={eNoteData} eNoteDoc={eNoteDoc} /> : null;
};

export const renderSettlementOnlyDocumentCard = (
  settlementOnlyDocs: BlendBridge.API.Document[],
  auditEvents?: EClose.AuditEventResponse[],
): React.ReactNode => {
  return (
    <div className={css(cardWrapperStyles.singleCardWrapper)}>
      <DocumentsCard
        auditEvents={auditEvents}
        documents={settlementOnlyDocs}
        id="it-SettlementOnlyDocuments"
        isUploaded={false}
        key="it-SettlementOnlyDocuments"
        title={DocumentTitles.CLOSING_SETTLEMENT_ONLY}
      />
    </div>
  );
};

const getSignedDocIDsForRON = (auditEvents?: EClose.FullClosing['auditEvents']): string[] => {
  return chain(auditEvents)
    .filter({ type: constants.ECloseAuditEventNames.SIGNED_DOCUMENTS_RETRIEVED })
    .flatMap(({ meta }) => concat(meta?.retrievedDocumentIDs as string[], meta?.newDocumentIDs as string[]))
    .compact()
    .value();
};

export const partitionBySignedStatus = (
  receivedDocs: BlendBridge.API.Document[],
  docIdToRefMap: Dictionary<EClose.API.DocumentReferenceResponse>,
  auditEvents: EClose.AuditEventResponse[] | undefined,
): [BlendBridge.API.Document[], BlendBridge.API.Document[]] => {
  const signedDocIds = new Set(getSignedDocIDsForRON(auditEvents));
  // The receivedDocumentIds from Audit Event meta actually seems to match the
  // documents.primaryFile._id instead of document._id.
  // This PR (https://git.blendlabs.com/blend/eclose/pull/1821) should fix this issue, so
  // new closings will have proper `document._id`. However the old closings are not updated,
  // so, we would have to either run a back migration to update fields (EC-2939), or wait for these old
  // closings to be purged.
  return partition(receivedDocs, ({ _id, primaryFile }) => {
    const hasMatchingDocumentId = signedDocIds.has(_id);
    const hasDocumentWithMachingFileIdInDocumentReferences =
      primaryFile !== undefined && signedDocIds.has(primaryFile._id!) && _id in docIdToRefMap;
    const isRONActivityDoc = docIdToRefMap[_id]?.documentType === ECloseDocumentTypes.RON_ACTIVITY_LOG;
    return hasMatchingDocumentId || hasDocumentWithMachingFileIdInDocumentReferences || isRONActivityDoc;
  });
};

const renderDocsWhenClosingStatusIncomplete = (
  receivedDocs: BlendBridge.API.Document[],
  docIdToRefMap: Dictionary<EClose.API.DocumentReferenceResponse>,
  loan: BlendBridge.API.Loan,
  auditEvents: EClose.AuditEventResponse[] | undefined,
  documentTypeToClosingDesignationsMap: Record<string, EClose.DocumentClosingDesignation[]>,
  signAheadDocsTag?: JSX.Element,
  ronDocsTag?: JSX.Element,
): JSX.Element | null => {
  const [signedDocuments, otherReceivedDocuments] = partitionBySignedStatus(receivedDocs, docIdToRefMap, auditEvents);
  const unsignedRonClosingDocs = docUtils.filterDocsByDesignations(
    otherReceivedDocuments,
    docIdToRefMap,
    documentTypeToClosingDesignationsMap.ronClosingDocs,
  );
  const ronClosingDocsReceived = [...signedDocuments, ...unsignedRonClosingDocs];
  const signAheadDocsReceived = docUtils.filterDocsByDesignations(
    otherReceivedDocuments,
    docIdToRefMap,
    documentTypeToClosingDesignationsMap.signAheadDocs,
  );
  return (
    <>
      {renderReceivedDocsOfType({
        auditEvents,
        docDesignation: 'SIGN_AHEAD',
        documents: signAheadDocsReceived,
        statusTag: signAheadDocsTag,
      })}
      {renderReceivedDocsOfType({
        auditEvents,
        docDesignation: 'DAY_OF',
        documents: ronClosingDocsReceived,
        label: 'RON',
        statusTag: ronDocsTag,
      })}
    </>
  );
};

const renderDocsWhenClosingStatusComplete = (
  receivedDocs: BlendBridge.API.Document[],
  docIdToRefMap: Dictionary<EClose.API.DocumentReferenceResponse>,
  loan: BlendBridge.API.Loan,
  auditEvents: EClose.AuditEventResponse[] | undefined,
  documenTypeToClosingDesignationsMap: Record<string, EClose.DocumentClosingDesignation[]>,
  signAheadDocsTag?: JSX.Element,
  ronDocsTag?: JSX.Element,
): JSX.Element | null => {
  const [signedDocuments, otherReceivedDocuments] = partitionBySignedStatus(receivedDocs, docIdToRefMap, auditEvents);

  const otherRONClosingDocsReceived = docUtils.filterDocsByDesignations(
    otherReceivedDocuments,
    docIdToRefMap,
    documenTypeToClosingDesignationsMap.ronClosingDocs,
  );
  const signAheadDocsReceived = docUtils.filterDocsByDesignations(
    otherReceivedDocuments,
    docIdToRefMap,
    documenTypeToClosingDesignationsMap.signAheadDocs,
  );
  return (
    <>
      {renderReceivedDocsOfType({
        auditEvents,
        docDesignation: 'SIGN_AHEAD',
        documents: signAheadDocsReceived,
        statusTag: signAheadDocsTag,
      })}
      {renderReceivedDocsOfType({
        auditEvents,
        docDesignation: 'DAY_OF',
        documents: signedDocuments,
        label: 'RON',
        statusTag: ronDocsTag,
      })}
      {renderReceivedDocsOfType({
        auditEvents,
        docDesignation: 'REJECTED_RON',
        documents: otherRONClosingDocsReceived,
      })}
    </>
  );
};

const isNonIntegratedRON = (loan: BlendBridge.API.Loan): boolean => {
  const derivedClosingType = loan.closing?.ronSelectionData?.derivedClosingType;
  const closingType = loan.closing?.closingType;
  // EClose treats NonIntegratedClosings as HYBRID
  return closingType === 'RON' && derivedClosingType === 'HYBRID';
};

/**
 * Function that derives the Tag for SA and OpsDesk views of eSign documents based on closing type. For non-integrated RON closing
 * notary session will occur outside of Blend, hence we return undefined.
 * @param loan
 * @returns JSX.Element | undefined
 */
export const getSignAheadDocStatusTag = (loan: BlendBridge.API.Loan): JSX.Element | undefined => {
  if (loan.closing === undefined) return;

  switch (loan.closing?.closingType) {
    case 'HYBRID':
      if (loan.areSignAheadDocsSigned !== undefined) {
        return loan.areSignAheadDocsSigned ? constants.SIGNATURE_COMPLETED_TAG : constants.PENDING_SIGNATURE_TAG;
      }
      break;
    case 'RON':
      if (loan?.featureFlags?.shouldSendSignAheadDocsToRON) {
        if (isNonIntegratedRON(loan)) {
          // closing will be completed externally display no status
          return undefined;
        }
        return isRONTransactionCompleted(loan) ? constants.SIGNATURE_COMPLETED_TAG : constants.PENDING_SIGNATURE_TAG;
      }

      if (loan.areSignAheadDocsSigned !== undefined) {
        return loan.areSignAheadDocsSigned ? constants.SIGNATURE_COMPLETED_TAG : constants.PENDING_SIGNATURE_TAG;
      }
      break;
  }

  return undefined;
};

const getRonDocumentsTags = (loan: BlendBridge.API.Loan) => {
  const signAheadDocsTag = getSignAheadDocStatusTag(loan);
  // On completion of RON session where all documents are signed, closingStatus is changed to COMPLETED
  // Thus, we assume signatures are completed.
  const ronDocsTag = isRONTransactionCompleted(loan)
    ? constants.SIGNATURE_COMPLETED_TAG
    : constants.PENDING_SIGNATURE_TAG;

  return { ronDocsTag, signAheadDocsTag };
};

export const renderReceivedRONClosingDocuments = (
  receivedDocs: BlendBridge.API.Document[],
  docIdToRefMap: Dictionary<EClose.API.DocumentReferenceResponse>,
  loan: BlendBridge.API.Loan,
  auditEvents: EClose.AuditEventResponse[] | undefined,
): JSX.Element | null => {
  const { signAheadDocsTag, ronDocsTag } = getRonDocumentsTags(loan);

  const documenTypeToClosingDesignationsMap: Record<string, EClose.DocumentClosingDesignation[]> = loan.featureFlags
    ?.shouldSendSignAheadDocsToRON
    ? {
        ronClosingDocs: ['DAY_OF', 'SIGN_AHEAD', 'UNDETERMINED', 'RON'],
        signAheadDocs: [],
      }
    : {
        ronClosingDocs: ['DAY_OF', 'UNDETERMINED', 'RON'],
        signAheadDocs: ['SIGN_AHEAD'],
      };

  const [titleDocs, otherReceivedDocs] = partition(
    receivedDocs,
    ({ type }) => type === BlendBridge.FileTypes.TITLE_DOCUMENT,
  );

  const ronSessionCompleted = isRONTransactionCompleted(loan);

  const signatureTag = ronSessionCompleted ? constants.SIGNATURE_COMPLETED_TAG : constants.PENDING_SIGNATURE_TAG;

  const closingDesignationDocs = ronSessionCompleted
    ? renderDocsWhenClosingStatusComplete(
        otherReceivedDocs,
        docIdToRefMap,
        loan,
        auditEvents,
        documenTypeToClosingDesignationsMap,
        signAheadDocsTag,
        ronDocsTag,
      )
    : renderDocsWhenClosingStatusIncomplete(
        otherReceivedDocs,
        docIdToRefMap,
        loan,
        auditEvents,
        documenTypeToClosingDesignationsMap,
        signAheadDocsTag,
        ronDocsTag,
      );

  return (
    <>
      {closingDesignationDocs}
      {!isEmpty(titleDocs) && (
        <div className={css(cardWrapperStyles.singleCardWrapper)}>
          <DocumentsCard
            documents={titleDocs}
            headerChildren={signatureTag}
            id="it-TitleDocuments"
            title="Title Documents"
          />
        </div>
      )}
    </>
  );
};

export const renderReceivedRONClosingDocumentsOldVersion = (
  receivedDocs: BlendBridge.API.Document[],
  docIdToRefMap: Dictionary<EClose.API.DocumentReferenceResponse>,
  loan: BlendBridge.API.Loan,
  auditEvents: EClose.AuditEventResponse[] | undefined,
): JSX.Element | null => {
  const { signAheadDocsTag, ronDocsTag } = getRonDocumentsTags(loan);

  const [signedDocuments, otherReceivedDocuments] = partitionBySignedStatus(receivedDocs, docIdToRefMap, auditEvents);
  const undeterminedDocs = docUtils.filterDocsByDesignations(otherReceivedDocuments, docIdToRefMap, ['UNDETERMINED']);
  const signAheadDocsReceived = docUtils.filterDocsByDesignations(otherReceivedDocuments, docIdToRefMap, [
    'SIGN_AHEAD',
  ]);
  const dayOfDocsReceived = docUtils.filterDocsByDesignations(otherReceivedDocuments, docIdToRefMap, ['DAY_OF']);

  return (
    <>
      {renderReceivedDocsOfType({ auditEvents, docDesignation: 'SIGNED', documents: signedDocuments })}
      {renderReceivedDocsOfType({ auditEvents, docDesignation: 'UNDETERMINED', documents: undeterminedDocs })}
      {renderReceivedDocsOfType({
        auditEvents,
        docDesignation: 'SIGN_AHEAD',
        documents: signAheadDocsReceived,
        statusTag: signAheadDocsTag,
      })}
      {renderReceivedDocsOfType({
        auditEvents,
        docDesignation: 'DAY_OF',
        documents: dayOfDocsReceived,
        label: 'Lender RON',
        statusTag: ronDocsTag,
      })}
    </>
  );
};

export const renderPreClosingDocuments = (
  preClosingDocuments: BlendBridge.API.Document[],
  auditEvents?: EClose.AuditEventResponse[],
): React.ReactNode => {
  return (
    !isEmpty(preClosingDocuments) && (
      <div className={css(cardWrapperStyles.singleCardWrapper)}>
        <DocumentsCard
          auditEvents={auditEvents}
          closingDesignation="PRE_CLOSING"
          documentTypeLabel={EcloseDocumentTypeLabels.PRELIMINARY_CLOSING_DOCUMENTS}
          documents={preClosingDocuments}
          id="it-LenderPreliminaryClosing"
          isUploaded={false}
          key="it-LenderPreliminaryClosing"
          title={DocumentTitles.LENDER_PRELIMINARY_CLOSING_DOCUMENTS}
        />
      </div>
    )
  );
};

export const renderAdditionalDocuments = (
  documents: BlendBridge.API.Document[],
  removeDoc?: (id: string) => void,
): React.ReactNode => {
  const {
    documentTypeConstants: { additionalBorrowerSignedDocs, sortedAdditionalDocTypeList },
  } = getConfig();

  return sortedAdditionalDocTypeList.map(docType => {
    const docs = documents.filter(({ type }: Partial<BlendBridge.API.Document>) => docType === type);
    if (!docs.length) {
      return null;
    }
    const firstDoc = docs[0];
    const title = docType === additionalBorrowerSignedDocs ? 'Borrower-Signed Closing Documents' : titleCase(docType);

    return (
      <div className={css(cardWrapperStyles.singleCardWrapper)} key={firstDoc._id}>
        <DocumentsCard
          documents={docs}
          id={`it-${kebabCase(title)}-${firstDoc._id}`}
          isUploaded={true}
          removeDoc={removeDoc}
          title={title}
        />
      </div>
    );
  });
};
